import { ReactNode, useRef, useState } from "react";
import styled from "styled-components";

import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Button } from "@pages/AdminDashboard/ui/Button";
import { CheckboxCard } from "@pages/AdminDashboard/ui/CheckboxCard";
import { DateRange, DateRangeValue, isDateRangeInvalid } from "@pages/AdminDashboard/ui/DateRange";
import { RadioCardGroup, RadioCardOption } from "@pages/AdminDashboard/ui/RadioCardGroup";
import { SearchInput } from "@pages/AdminDashboard/ui/SearchInput";
import { Select } from "@pages/AdminDashboard/ui/Select";
import { TextField } from "@pages/AdminDashboard/ui/TextField";
import { UiKitSection } from "@pages/AdminDashboard/ui/UiKitSection";

const [spaceXs, , spaceMd, , spaceXl] = adminTokens.spacing;
const noop = () => undefined;

const Grid = styled.div<{ $columnWidth?: number }>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(min(${({ $columnWidth }) => $columnWidth ?? 360}px, 100%), 1fr)
  );
  gap: ${spaceMd}px;
  margin-bottom: ${spaceXl}px;
`;

const Box = styled.div.attrs({ role: "group" })`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spaceMd}px;
  border: 1px solid ${adminTokens.color.border};
  border-radius: ${adminTokens.radius.row}px;
  padding: ${spaceMd}px;
`;

const Content = styled.div`
  align-self: stretch;
`;

const Caption = styled.span`
  color: ${adminTokens.color.inkSoft};
  font-family: ${adminTokens.typeScale.meta.fontFamily};
  font-size: ${adminTokens.typeScale.meta.fontSize};
`;

interface ExampleProps {
  name: string;
  children: ReactNode;
}

const Example = ({ name, children }: ExampleProps) => (
  <Box aria-label={name}>
    <Caption>{name}</Caption>
    <Content>{children}</Content>
  </Box>
);

// A static page cannot hold the keyboard focus, so a "Focused" example has a Button
// that moves the focus to its control and shows the focus outline (FORMGAL-R1…R6).
const FocusableExample = ({ name, children }: ExampleProps) => {
  const boxRef = useRef<HTMLDivElement>(null);

  const focusControl = () =>
    boxRef.current
      ?.querySelector<HTMLElement>("input:checked, input:not([type='radio']), select")
      ?.focus();

  return (
    <Example name={name}>
      <div ref={boxRef}>{children}</div>
      <Button look="secondary" onClick={focusControl}>
        Focus
      </Button>
    </Example>
  );
};

const statusOptions = [
  "created",
  "assigned",
  "processing",
  "waiting",
  "support",
  "completed",
  "cancelled",
].map((status) => ({ value: status, label: status }));

export const TextFieldSection = () => (
  <UiKitSection title="Text field">
    <Grid>
      <Example name="Default">
        <TextField label="External Case ID" placeholder="Exact match" />
      </Example>
      <FocusableExample name="Focused">
        <TextField label="External Case ID" placeholder="Exact match" />
      </FocusableExample>
      <Example name="Filled">
        <TextField label="External Case ID" defaultValue="CASE-TEST-1042" />
      </Example>
      <Example name="Error">
        <TextField label="Street" placeholder="Street" error="Enter a street" />
      </Example>
      <Example name="Disabled">
        <TextField label="External Case ID" defaultValue="CASE-TEST-1042" disabled />
      </Example>
    </Grid>
  </UiKitSection>
);

const searchPlaceholder = "Find a User by email, or an Order by Order ID or Case ID";

export const SearchInputSection = () => (
  <UiKitSection title="Search input">
    <Grid>
      <Example name="Default">
        <SearchInput
          label="Find a User or an Order"
          placeholder={searchPlaceholder}
          onSearch={noop}
        />
      </Example>
      <FocusableExample name="Focused">
        <SearchInput
          label="Find a User or an Order"
          placeholder={searchPlaceholder}
          onSearch={noop}
        />
      </FocusableExample>
      <Example name="Filled">
        <SearchInput
          label="Find a User or an Order"
          defaultValue="test.traveler@example.com"
          onSearch={noop}
        />
      </Example>
      <Example name="Disabled">
        <SearchInput
          label="Find a User or an Order"
          defaultValue="test.traveler@example.com"
          disabled
          onSearch={noop}
        />
      </Example>
      <Example name="With Enter hint">
        <SearchInput
          label="Find a User or an Order"
          placeholder={searchPlaceholder}
          hasEnterHint
          onSearch={noop}
        />
      </Example>
      <Example name="With action">
        <SearchInput
          label="Find a User or an Order"
          placeholder="Email or User ID"
          onSearch={noop}
          action={{ label: "Search" }}
        />
      </Example>
    </Grid>
  </UiKitSection>
);

export const SelectSection = () => (
  <UiKitSection title="Select">
    <Grid>
      <Example name="Default">
        <Select label="Status" options={statusOptions} width={150} />
      </Example>
      <FocusableExample name="Focused">
        <Select label="Status" options={statusOptions} width={150} />
      </FocusableExample>
      <Example name="Filled">
        <Select label="Status" options={statusOptions} defaultValue="support" width={150} />
      </Example>
      <Example name="Error">
        <Select
          label="State"
          options={[{ value: "", label: "Choose a state" }]}
          error="Choose a state"
          width={150}
        />
      </Example>
      <Example name="Disabled">
        <Select
          label="Status"
          options={statusOptions}
          defaultValue="support"
          disabled
          width={150}
        />
      </Example>
    </Grid>
  </UiKitSection>
);

interface RangeExampleProps {
  name: string;
  initial: DateRangeValue;
}

const RangeExample = ({ name, initial }: RangeExampleProps) => {
  const [range, setRange] = useState(initial);

  return (
    <Example name={name}>
      <DateRange
        fromLabel="Created from"
        toLabel="to"
        from={range.from}
        to={range.to}
        onChange={setRange}
      >
        <Button look="primary" disabled={isDateRangeInvalid(range.from, range.to)}>
          Search
        </Button>
      </DateRange>
    </Example>
  );
};

export const DateRangeSection = () => (
  <UiKitSection title="Date range">
    <Grid>
      <RangeExample name="Valid range" initial={{ from: "2026-09-01", to: "2026-09-03" }} />
      <RangeExample name="Invalid range" initial={{ from: "2026-09-03", to: "2026-09-01" }} />
    </Grid>
  </UiKitSection>
);

const prescription = {
  title: "Acetazolamide",
  description: "125 mg",
  detail: "20 tablets · 10 days",
};

export const CheckboxCardSection = () => (
  <UiKitSection title="Checkbox card">
    <Grid>
      <Example name="Default">
        <CheckboxCard {...prescription} />
      </Example>
      <FocusableExample name="Focused">
        <CheckboxCard {...prescription} />
      </FocusableExample>
      <Example name="Checked">
        <CheckboxCard {...prescription} defaultChecked />
      </Example>
      <Example name="Disabled">
        <CheckboxCard {...prescription} disabled />
      </Example>
    </Grid>
  </UiKitSection>
);

const shippingMethods: RadioCardOption[] = [
  { value: "ups_second_day_air", label: "ups_second_day_air" },
  { value: "ups_next_day_air", label: "ups_next_day_air", tag: "Current" },
  { value: "standard", label: "standard" },
  { value: "priority", label: "priority" },
];

interface RadioExampleProps {
  name: string;
  options?: RadioCardOption[];
  initial?: string;
  error?: string;
  isFocusable?: boolean;
}

const RadioExample = ({
  name,
  options = shippingMethods,
  initial = "",
  error,
  isFocusable = false,
}: RadioExampleProps) => {
  const [value, setValue] = useState(initial);
  const group = (
    <RadioCardGroup
      legend="Shipping method"
      name={`shipping-method-${name.toLowerCase()}`}
      options={options}
      value={value}
      onChange={setValue}
      error={error}
    />
  );

  return isFocusable ? (
    <FocusableExample name={name}>{group}</FocusableExample>
  ) : (
    <Example name={name}>{group}</Example>
  );
};

// The shipping method cards are wide in the design, so each example gets room for two columns.
const radioColumnWidth = 520;

export const RadioCardGroupSection = () => (
  <UiKitSection title="Radio card group">
    <Grid $columnWidth={radioColumnWidth}>
      <RadioExample name="Default" />
      <RadioExample name="Focused" initial="ups_next_day_air" isFocusable />
      <RadioExample name="Selected" initial="ups_next_day_air" />
      <RadioExample name="Error" error="Choose a method" />
      <RadioExample
        name="Disabled"
        initial="standard"
        options={shippingMethods.map((option) =>
          option.value === "ups_next_day_air" || option.value === "priority"
            ? { ...option, isDisabled: true }
            : option
        )}
      />
    </Grid>
  </UiKitSection>
);
